import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useMemo } from "react";
import { Colors } from "../../constants/colors";
// ═══════════════════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════════════════
const PencilIcon = ({ className, color = Colors.Gray400 }) => (_jsx("svg", { className: className, width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M11.3333 2.00001C11.5084 1.82491 11.7163 1.68602 11.9451 1.59126C12.1739 1.49651 12.4191 1.44775 12.6667 1.44775C12.9143 1.44775 13.1595 1.49651 13.3883 1.59126C13.6171 1.68602 13.8249 1.82491 14 2.00001C14.1751 2.17511 14.314 2.38298 14.4087 2.61177C14.5035 2.84056 14.5523 3.08576 14.5523 3.33334C14.5523 3.58093 14.5035 3.82613 14.4087 4.05492C14.314 4.28371 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z", stroke: color, strokeWidth: "1.33333", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const PlusIcon = ({ className, color = Colors.Gray600 }) => (_jsxs("svg", { className: className, width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: [_jsx("path", { d: "M8 3.33334V12.6667", stroke: color, strokeWidth: "1.33333", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M3.33334 8H12.6667", stroke: color, strokeWidth: "1.33333", strokeLinecap: "round", strokeLinejoin: "round" })] }));
const CheckIcon = ({ className, color = "white" }) => (_jsx("svg", { className: className, width: "16", height: "16", viewBox: "0 0 12 12", fill: "none", children: _jsx("path", { d: "M10 3L4.5 8.5L2 6", stroke: color, strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const XIcon = ({ className, color = Colors.Gray500 }) => (_jsxs("svg", { className: className, width: "16", height: "16", viewBox: "0 0 12 12", fill: "none", children: [_jsx("path", { d: "M9 3L3 9", stroke: color, strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M3 3L9 9", stroke: color, strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" })] }));
// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Check if a string looks like a file path (not editable)
 */
const isFilePath = (text) => {
    return /^([A-Za-z]:\\|\/|\.\.?\/|~\/)/.test(text) ||
        /\.(xlsx|xls|csv|pdf|docx|pptx|txt|json)$/i.test(text);
};
/**
 * Get display text for an answer
 */
const getAnswerDisplay = (answer) => {
    if (answer.skipped)
        return "Skipped";
    if (answer.letCarouselDecide)
        return "Let Carousel decide";
    if (Array.isArray(answer.response)) {
        return answer.response.length > 0 ? answer.response.join(", ") : "No selection";
    }
    return answer.response || "No selection";
};
// ═══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════
/** Numbered badge for multiple_choice options */
const NumberBadge = ({ number }) => (_jsx("div", { className: "flex w-4 items-center justify-center gap-0.5 px-1 py-px rounded text-micro-bold shrink-0", style: {
        backgroundColor: Colors.Gray100,
        border: `1px solid ${Colors.Gray300}`,
        color: Colors.Gray900,
        lineHeight: '1.5',
    }, children: _jsx("span", { style: { marginTop: '-1px' }, children: number }) }));
/** Checkbox for multiple_selection options */
const Checkbox = ({ checked }) => (_jsx("div", { className: "flex items-center justify-center rounded-[3.2px] shrink-0 transition-all", style: {
        width: '16px',
        height: '16px',
        backgroundColor: checked ? Colors.PrimaryPurple60 : 'white',
        border: `1px solid ${checked ? Colors.PrimaryPurple60 : Colors.Gray400}`,
    }, children: checked && _jsx(CheckIcon, { color: "white" }) }));
/** Question badge (Q1, Q2, etc.) */
const QuestionBadge = ({ number, variant }) => {
    const styles = {
        active: {
            backgroundColor: Colors.PrimaryPurple10,
            borderColor: Colors.PrimaryPurple30,
            color: Colors.PrimaryPurple70,
        },
        pending: {
            backgroundColor: Colors.Gray100,
            borderColor: Colors.Gray300,
            color: Colors.Gray600,
        },
        answered: {
            backgroundColor: Colors.Gray100,
            borderColor: Colors.Gray300,
            color: Colors.Gray600,
        },
    };
    const style = styles[variant];
    return (_jsx("div", { className: "inline-flex items-center gap-0.5 px-1 py-px rounded text-micro-bold shrink-0", style: {
            backgroundColor: style.backgroundColor,
            border: `1px solid ${style.borderColor}`,
            color: style.color,
            lineHeight: '1.5',
        }, children: _jsxs("span", { style: { marginTop: '-1px' }, children: ["Q", number] }) }));
};
const OptionRow = ({ option, index, isSelected, isEditing, editValue, type, canEdit, onSelect, onStartEdit, onEditChange, onEditConfirm, onEditCancel, }) => {
    const isMultipleChoice = type === 'multiple_choice';
    return (_jsx("div", { className: "flex flex-col items-start px-0 py-1 border-b group w-full self-stretch", style: { borderColor: Colors.Gray200 }, children: _jsxs("div", { className: "flex items-start gap-1.5 px-1 py-1 w-full rounded-[5px] cursor-pointer transition-colors", style: {
                backgroundColor: isSelected && isMultipleChoice ? Colors.PrimaryPurple05 : 'transparent',
                border: isSelected && isMultipleChoice ? `1px solid ${Colors.PrimaryPurple15}` : '1px solid transparent',
            }, onMouseEnter: (e) => {
                if (!isSelected || !isMultipleChoice) {
                    e.currentTarget.style.backgroundColor = Colors.Gray100;
                    e.currentTarget.style.border = `1px solid ${Colors.Gray200}`;
                }
            }, onMouseLeave: (e) => {
                if (!isSelected || !isMultipleChoice) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.border = '1px solid transparent';
                }
            }, onClick: !isEditing ? onSelect : undefined, children: [_jsx("div", { className: "shrink-0 w-4 h-[18px] flex items-center justify-center", children: isMultipleChoice ? (_jsx(NumberBadge, { number: index + 1 })) : (_jsx(Checkbox, { checked: isSelected })) }), isEditing ? (_jsxs("div", { className: "flex-1 flex items-center gap-2 px-1", children: [_jsx("input", { type: "text", value: editValue, onChange: (e) => onEditChange(e.target.value), onKeyDown: (e) => {
                                if (e.key === 'Enter')
                                    onEditConfirm();
                                if (e.key === 'Escape')
                                    onEditCancel();
                            }, className: "flex-1 px-2 py-1 rounded text-helper", style: {
                                border: `1px solid ${Colors.Gray300}`,
                                outline: 'none',
                            }, autoFocus: true, onClick: (e) => e.stopPropagation() }), _jsx("button", { onClick: (e) => { e.stopPropagation(); onEditConfirm(); }, className: "flex items-center justify-center rounded", style: {
                                backgroundColor: Colors.Green10,
                                width: '16px',
                                height: '16px',
                            }, children: _jsx(CheckIcon, { color: Colors.Green60 }) }), _jsx("button", { onClick: (e) => { e.stopPropagation(); onEditCancel(); }, className: "flex items-center justify-center rounded", style: {
                                width: '16px',
                                height: '16px',
                                backgroundColor: Colors.Red10,
                            }, children: _jsx(XIcon, { color: Colors.Red60 }) })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex-1 flex items-center px-[2px]", children: _jsx("p", { className: "text-helper leading-[1.5]", style: { color: Colors.Gray990, margin: 0 }, children: option }) }), canEdit && (_jsx("div", { className: "shrink-0 w-4 h-[18px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity", children: _jsx("button", { onClick: (e) => { e.stopPropagation(); onStartEdit(); }, className: "hover:opacity-80 transition-opacity leading-none flex", title: "Edit option", onMouseEnter: (e) => {
                                    const svg = e.currentTarget.querySelector('svg path');
                                    if (svg)
                                        svg.setAttribute('stroke', Colors.Gray600);
                                }, onMouseLeave: (e) => {
                                    const svg = e.currentTarget.querySelector('svg path');
                                    if (svg)
                                        svg.setAttribute('stroke', Colors.Gray400);
                                }, children: _jsx(PencilIcon, { color: Colors.Gray400 }) }) }))] }))] }) }));
};
/** Collapsed/pending question row */
const CollapsedQuestionRow = ({ question, answer, questionNumber, onClick, variant }) => {
    const hasAnswer = answer && (answer.letCarouselDecide ||
        answer.skipped ||
        (Array.isArray(answer.response) ? answer.response.length > 0 : answer.response !== ""));
    return (_jsxs("div", { className: "flex flex-col items-start px-2 py-[6px] gap-[6px]", style: {
            backgroundColor: variant === 'answered' ? 'white' : Colors.Gray100,
            borderBottom: `1px solid ${Colors.Gray300}`,
            cursor: variant === 'answered' ? 'pointer' : 'default',
        }, onClick: variant === 'answered' ? onClick : undefined, children: [_jsxs("div", { className: "flex items-start gap-1 w-full", children: [_jsx("div", { className: "shrink-0 h-[18px] flex items-center", children: _jsx(QuestionBadge, { number: questionNumber, variant: variant }) }), _jsx("div", { className: "flex-1 px-[2px]", children: _jsx("p", { className: "text-helper leading-[1.5]", style: { color: Colors.Gray500, margin: 0 }, children: question.prompt }) })] }), hasAnswer && answer && (_jsx("p", { className: "text-helper-bold leading-[1.5]", style: { color: Colors.Gray800 }, children: getAnswerDisplay(answer) }))] }));
};
// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export const UserInterviewBlock = ({ data, onSubmit, }) => {
    // Track which question is expanded
    const [expandedIndex, setExpandedIndex] = useState(data.currentQuestionIndex);
    const [answers, setAnswers] = useState(data.answers);
    const [customOptions, setCustomOptions] = useState(new Map());
    const [editedOptions, setEditedOptions] = useState(new Map());
    const [addingCustom, setAddingCustom] = useState(false);
    const [customInput, setCustomInput] = useState("");
    const [editingIndex, setEditingIndex] = useState(null);
    const [editValue, setEditValue] = useState("");
    const expandedQuestion = data.questions[expandedIndex];
    const isCompleted = data.status === 'completed';
    // Current answer for expanded question
    const currentAnswer = answers.find(a => a.questionId === expandedQuestion?.id);
    const selectedOptions = useMemo(() => {
        if (!currentAnswer || currentAnswer.letCarouselDecide)
            return new Set();
        if (Array.isArray(currentAnswer.response)) {
            return new Set(currentAnswer.response);
        }
        return new Set([currentAnswer.response]);
    }, [currentAnswer]);
    // Check if current question has a selection (for Continue button)
    const hasCurrentSelection = currentAnswer && (currentAnswer.letCarouselDecide ||
        (Array.isArray(currentAnswer.response) ? currentAnswer.response.length > 0 : currentAnswer.response !== ""));
    // All options including custom ones, with edits applied
    const allOptions = useMemo(() => {
        if (!expandedQuestion)
            return [];
        const custom = customOptions.get(expandedQuestion.id) || [];
        const edits = editedOptions.get(expandedQuestion.id);
        const baseOptions = expandedQuestion.options.map((opt, idx) => {
            if (edits?.has(idx)) {
                return edits.get(idx);
            }
            return opt;
        });
        return [...baseOptions, ...custom];
    }, [expandedQuestion, customOptions, editedOptions]);
    // Handle option selection
    const handleSelect = useCallback((option) => {
        if (isCompleted || !expandedQuestion)
            return;
        setAnswers(prev => {
            const existing = prev.find(a => a.questionId === expandedQuestion.id);
            if (expandedQuestion.type === 'multiple_choice') {
                // Single selection - replace
                const newAnswer = {
                    questionId: expandedQuestion.id,
                    response: option,
                    letCarouselDecide: false,
                };
                const newAnswers = existing
                    ? prev.map(a => a.questionId === expandedQuestion.id ? newAnswer : a)
                    : [...prev, newAnswer];
                // Auto-advance for multiple_choice
                if (expandedIndex < data.questions.length - 1) {
                    setTimeout(() => {
                        setExpandedIndex(expandedIndex + 1);
                    }, 500);
                }
                else {
                    // Last question - submit
                    setTimeout(() => {
                        onSubmit(newAnswers);
                    }, 500);
                }
                return newAnswers;
            }
            else {
                // Multiple selection - toggle (NO auto-advance)
                const currentSelections = existing && Array.isArray(existing.response)
                    ? existing.response
                    : [];
                const newSelections = currentSelections.includes(option)
                    ? currentSelections.filter(s => s !== option)
                    : [...currentSelections, option];
                const newAnswer = {
                    questionId: expandedQuestion.id,
                    response: newSelections,
                    letCarouselDecide: false,
                };
                return existing
                    ? prev.map(a => a.questionId === expandedQuestion.id ? newAnswer : a)
                    : [...prev, newAnswer];
            }
        });
    }, [expandedQuestion, isCompleted, expandedIndex, data.questions.length, onSubmit]);
    // Handle "Let Carousel decide"
    const handleLetCarouselDecide = useCallback(() => {
        if (isCompleted || !expandedQuestion)
            return;
        setAnswers(prev => {
            const newAnswer = {
                questionId: expandedQuestion.id,
                response: expandedQuestion.type === 'multiple_choice' ? "" : [],
                letCarouselDecide: true,
            };
            const existing = prev.find(a => a.questionId === expandedQuestion.id);
            const newAnswers = existing
                ? prev.map(a => a.questionId === expandedQuestion.id ? newAnswer : a)
                : [...prev, newAnswer];
            // Advance with delay
            if (expandedIndex < data.questions.length - 1) {
                setTimeout(() => {
                    setExpandedIndex(expandedIndex + 1);
                }, 500);
            }
            else {
                setTimeout(() => {
                    onSubmit(newAnswers);
                }, 500);
            }
            return newAnswers;
        });
    }, [expandedQuestion, isCompleted, expandedIndex, data.questions.length, onSubmit]);
    // Handle skip - marks question as skipped (different from let carousel decide)
    const handleSkip = useCallback(() => {
        if (isCompleted || !expandedQuestion)
            return;
        setAnswers(prev => {
            const newAnswer = {
                questionId: expandedQuestion.id,
                response: expandedQuestion.type === 'multiple_choice' ? "" : [],
                skipped: true,
            };
            const existing = prev.find(a => a.questionId === expandedQuestion.id);
            const newAnswers = existing
                ? prev.map(a => a.questionId === expandedQuestion.id ? newAnswer : a)
                : [...prev, newAnswer];
            // Advance with delay
            if (expandedIndex < data.questions.length - 1) {
                setTimeout(() => {
                    setExpandedIndex(expandedIndex + 1);
                }, 500);
            }
            else {
                setTimeout(() => {
                    onSubmit(newAnswers);
                }, 500);
            }
            return newAnswers;
        });
    }, [expandedQuestion, isCompleted, expandedIndex, data.questions.length, onSubmit]);
    // Handle Continue for multiple selection
    const handleContinue = useCallback(() => {
        if (!hasCurrentSelection || isCompleted)
            return;
        if (expandedIndex < data.questions.length - 1) {
            setExpandedIndex(expandedIndex + 1);
        }
        else {
            // Last question - submit
            onSubmit(answers);
        }
    }, [hasCurrentSelection, isCompleted, expandedIndex, data.questions.length, answers, onSubmit]);
    // Handle add custom option
    const handleAddCustom = useCallback(() => {
        if (!customInput.trim() || !expandedQuestion)
            return;
        const trimmedInput = customInput.trim();
        setCustomOptions(prev => {
            const existing = prev.get(expandedQuestion.id) || [];
            const updated = new Map(prev);
            updated.set(expandedQuestion.id, [...existing, trimmedInput]);
            return updated;
        });
        // Auto-select the new option
        handleSelect(trimmedInput);
        setCustomInput("");
        setAddingCustom(false);
    }, [customInput, expandedQuestion, handleSelect]);
    // Handle edit confirm
    const handleEditConfirm = useCallback((optionIndex) => {
        if (!expandedQuestion || !editValue.trim()) {
            setEditingIndex(null);
            setEditValue("");
            return;
        }
        const oldOption = allOptions[optionIndex];
        const newOption = editValue.trim();
        // Update edited options map
        setEditedOptions(prev => {
            const questionEdits = new Map(prev.get(expandedQuestion.id) || []);
            questionEdits.set(optionIndex, newOption);
            const updated = new Map(prev);
            updated.set(expandedQuestion.id, questionEdits);
            return updated;
        });
        // Update selection if the edited option was selected
        if (selectedOptions.has(oldOption)) {
            setAnswers(prev => {
                const existing = prev.find(a => a.questionId === expandedQuestion.id);
                if (!existing)
                    return prev;
                if (expandedQuestion.type === 'multiple_choice') {
                    return prev.map(a => a.questionId === expandedQuestion.id
                        ? { ...a, response: newOption }
                        : a);
                }
                else {
                    const newSelections = existing.response.map(s => s === oldOption ? newOption : s);
                    return prev.map(a => a.questionId === expandedQuestion.id
                        ? { ...a, response: newSelections }
                        : a);
                }
            });
        }
        setEditingIndex(null);
        setEditValue("");
    }, [expandedQuestion, editValue, allOptions, selectedOptions]);
    if (!expandedQuestion)
        return null;
    // Active state: 2px purple border with shadow
    // Completed state: 1px gray border, no shadow
    const containerStyle = isCompleted ? {
        backgroundColor: 'white',
        border: `1px solid ${Colors.Gray300}`,
    } : {
        backgroundColor: 'white',
        border: `2px solid ${Colors.PrimaryPurple40}`,
        boxShadow: '0px 0px 6px -4px rgba(0,0,0,0.1), 0px 4px 4px 0px rgba(0,0,0,0.06), 0px 0px 2px 0px rgba(0,0,0,0.06), 0px 0px 1px 0px rgba(0,0,0,0.04)',
    };
    // Render a single question (expanded or collapsed)
    const renderQuestion = (question, index) => {
        const isActive = !isCompleted && index === expandedIndex;
        const answer = answers.find(a => a.questionId === question.id);
        const hasAnswer = answer && (answer.letCarouselDecide ||
            answer.skipped ||
            (Array.isArray(answer.response) ? answer.response.length > 0 : answer.response !== ""));
        if (isActive) {
            // Expanded active question
            return (_jsxs("div", { className: "flex flex-col gap-1 p-1", style: { borderBottom: `1px solid ${Colors.Gray300}` }, children: [_jsxs("div", { className: "flex items-start gap-1 p-1", children: [_jsx("div", { className: "shrink-0 h-[18px] flex items-center", children: _jsx(QuestionBadge, { number: index + 1, variant: "active" }) }), _jsx("div", { className: "flex-1 px-[2px]", children: _jsx("p", { className: "text-helper-bold leading-[1.5]", style: { color: Colors.Gray990, margin: 0 }, children: question.prompt }) })] }), _jsxs("div", { className: "flex flex-col items-start px-1 py-0 w-full", children: [_jsxs("div", { className: "flex flex-col items-start w-full", children: [allOptions.map((option, idx) => {
                                        const canEdit = (question.allowEdit !== false) && !isFilePath(option);
                                        return (_jsx(OptionRow, { option: option, index: idx, isSelected: selectedOptions.has(option), isEditing: editingIndex === idx, editValue: editValue, type: question.type, canEdit: canEdit, onSelect: () => handleSelect(option), onStartEdit: () => {
                                                setEditingIndex(idx);
                                                setEditValue(option);
                                            }, onEditChange: setEditValue, onEditConfirm: () => handleEditConfirm(idx), onEditCancel: () => {
                                                setEditingIndex(null);
                                                setEditValue("");
                                            } }, `${question.id}-${idx}-${option}`));
                                    }), question.allowCustom !== false && (_jsxs("div", { className: "flex items-center px-0 py-1 w-full border-b", style: { borderColor: Colors.Gray200 }, children: [question.type === 'multiple_selection' && (_jsx("div", { className: "shrink-0 w-4 h-[18px] flex items-center justify-center mr-1", children: _jsx("div", { className: "w-4 h-4 rounded-[3.2px]", style: {
                                                        backgroundColor: 'white',
                                                        border: `1px solid ${Colors.Gray400}`,
                                                    } }) })), addingCustom ? (_jsxs("div", { className: "flex-1 flex items-center gap-2 px-1", children: [_jsx("input", { type: "text", value: customInput, onChange: (e) => setCustomInput(e.target.value), onKeyDown: (e) => {
                                                            if (e.key === 'Enter')
                                                                handleAddCustom();
                                                            if (e.key === 'Escape') {
                                                                setAddingCustom(false);
                                                                setCustomInput("");
                                                            }
                                                        }, placeholder: "Type your custom option...", className: "flex-1 px-2 py-1 rounded text-helper", style: {
                                                            border: `1px solid ${Colors.Gray300}`,
                                                            outline: 'none',
                                                        }, autoFocus: true }), _jsx("button", { onClick: handleAddCustom, disabled: !customInput.trim(), className: "px-2 py-1 rounded text-helper-bold", style: {
                                                            backgroundColor: customInput.trim() ? Colors.PrimaryPurple60 : Colors.Gray200,
                                                            color: customInput.trim() ? 'white' : Colors.Gray500,
                                                            cursor: customInput.trim() ? 'pointer' : 'not-allowed',
                                                        }, children: "Add" }), _jsx("button", { onClick: () => { setAddingCustom(false); setCustomInput(""); }, className: "px-2 py-1 text-helper", style: { color: Colors.Gray500 }, children: "Cancel" })] })) : (_jsxs("button", { onClick: () => setAddingCustom(true), className: "flex items-center gap-1.5 px-1 py-1 flex-1 rounded-[5px] transition-colors", style: { backgroundColor: 'transparent', border: '1px solid transparent' }, onMouseEnter: (e) => {
                                                    e.currentTarget.style.backgroundColor = Colors.Gray100;
                                                    e.currentTarget.style.border = `1px solid ${Colors.Gray200}`;
                                                }, onMouseLeave: (e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.border = '1px solid transparent';
                                                }, children: [_jsx("div", { className: "shrink-0 w-4 h-[18px] flex items-center justify-center", children: _jsx(PlusIcon, { color: Colors.Gray600 }) }), _jsx("span", { className: "text-helper-bold h-[18px] flex items-start", style: { color: Colors.Gray800 }, children: "Add custom option" })] }))] }))] }), _jsx("div", { className: "flex flex-col w-full items-start gap-1 px-1 pt-[10px] pb-[5px]", children: _jsxs("div", { className: "flex items-start gap-1 w-full", children: [_jsx("button", { onClick: handleLetCarouselDecide, className: "flex flex-1 flex-col items-center justify-center gap-2 px-2.5 py-[3.5px] rounded-md overflow-hidden cursor-pointer transition-colors", style: {
                                                backgroundColor: 'transparent',
                                                border: `1px solid ${Colors.Gray300}`,
                                            }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = Colors.Gray100; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; }, children: _jsx("div", { className: "inline-flex items-center justify-center gap-1", children: _jsx("div", { className: "inline-flex items-center justify-center gap-2.5 px-0 py-[2.5px]", children: _jsx("span", { className: "text-helper-bold", style: { color: Colors.Gray800 }, children: "Let Carousel decide" }) }) }) }), _jsx("button", { onClick: handleSkip, className: "inline-flex flex-col items-center justify-center gap-2 px-2.5 py-[3.5px] rounded-md overflow-hidden cursor-pointer transition-colors", style: {
                                                backgroundColor: 'transparent',
                                                border: `1px solid ${Colors.Gray300}`,
                                            }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = Colors.Gray100; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; }, children: _jsx("div", { className: "inline-flex items-center justify-center gap-1", children: _jsx("div", { className: "inline-flex items-center justify-center gap-2.5 px-0 py-[2.5px]", children: _jsx("span", { className: "text-helper", style: { color: Colors.Gray800 }, children: "Skip" }) }) }) })] }) }), question.type === 'multiple_selection' && (_jsx("div", { className: "flex flex-col w-full items-start gap-1 px-1 py-[5px]", children: _jsx("div", { className: "flex items-start gap-1 w-full", children: _jsx("button", { onClick: handleContinue, disabled: !hasCurrentSelection, className: "flex flex-1 flex-col items-center justify-center gap-2 px-2.5 py-[3.5px] rounded-md overflow-hidden transition-colors", style: {
                                            backgroundColor: 'transparent',
                                            border: `1px solid ${hasCurrentSelection ? Colors.Gray600 : Colors.Gray300}`,
                                            cursor: hasCurrentSelection ? 'pointer' : 'not-allowed',
                                            opacity: hasCurrentSelection ? 1 : 0.5,
                                        }, onMouseEnter: (e) => { if (hasCurrentSelection)
                                            e.currentTarget.style.backgroundColor = Colors.Gray100; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; }, children: _jsx("div", { className: "inline-flex items-center justify-center gap-1", children: _jsx("div", { className: "inline-flex items-center justify-center gap-2.5 px-0 py-[2.5px]", children: _jsx("span", { className: "text-helper-bold", style: { color: Colors.Gray800 }, children: "Continue" }) }) }) }) }) }))] })] }, question.id));
        }
        else {
            // Collapsed question
            return (_jsx(CollapsedQuestionRow, { question: question, answer: answer, questionNumber: index + 1, onClick: () => !isCompleted && setExpandedIndex(index), variant: hasAnswer ? 'answered' : 'pending' }, question.id));
        }
    };
    return (_jsx("div", { className: "flex flex-col rounded-lg overflow-hidden w-full", style: containerStyle, children: data.questions.map((question, index) => renderQuestion(question, index)) }));
};
export default UserInterviewBlock;
